import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  LinearProgress,
} from '@mui/material';
import {
  MusicNote as MusicNoteIcon,
  GraphicEq as GraphicEqIcon,
  Sensors as SensorsIcon,
  EventAvailable as EventAvailableIcon,
  Tune as TuneIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Responsive as ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import PageHeader from '../components/PageHeader';
import DashboardCustomize from '../components/DashboardCustomize';
import {
  GRID_BREAKPOINTS,
  GRID_COLS,
  GRID_MARGIN,
  GRID_ROW_HEIGHT,
  buildDashboardWidgets,
  buildDefaultLayouts,
  layoutsAreEqual,
  reconcileDashboardLayout,
  sizeForWidget,
  sortGridItems,
} from '../utils/dashboardWidgets';
import io from 'socket.io-client';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { mergeDashboardStatusSnapshot } from '../utils/dashboardStatus';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const PRIMARY_STATION_STORAGE_KEY = 'dashboardPrimaryStationId';
const LAYOUT_ORDER_STORAGE_KEY = 'dashboardLayoutOrder';
const HIDDEN_WIDGETS_STORAGE_KEY = 'dashboardHiddenWidgets';
const GRID_LAYOUTS_STORAGE_KEY = 'dashboardGridLayouts';
const ARTWORK_CACHE_STORAGE_KEY = 'dashboardArtworkCache';
const ARTWORK_CACHE_MAX_ENTRIES = 200;
const ARTWORK_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const ARTWORK_FETCH_MAX_ATTEMPTS = 3;

// Handles boolean values that may arrive as strings/numbers from the API
const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === 't';

const resolveMetadataArtworkUrl = (rawMetadata) => {
  if (!rawMetadata) return null;
  try {
    const metadata = typeof rawMetadata === 'string' ? JSON.parse(rawMetadata) : rawMetadata;
    const url = metadata.artwork_url
      || metadata.artworkUrl
      || metadata.image_url
      || metadata.imageUrl
      || metadata.cover_url
      || metadata.coverUrl
      || metadata.cover_art
      || metadata.coverArt
      || metadata.album_art
      || metadata.albumArt;
    if (typeof url !== 'string' || !url.trim()) return null;
    const normalizedUrl = url.trim();
    if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) return normalizedUrl;
    if (normalizedUrl.startsWith('//')) return `${window.location.protocol}${normalizedUrl}`;
    if (normalizedUrl.startsWith('data:') || normalizedUrl.startsWith('blob:')) return normalizedUrl;
    if (normalizedUrl.startsWith('/')) return `${API_URL.replace(/\/$/, '')}${normalizedUrl}`;
    if (normalizedUrl.includes('/') || normalizedUrl.includes('\\')) {
      const filename = normalizedUrl.split(/[/\\]/).pop();
      return `${API_URL}/uploads/${filename}`;
    }
    return `${API_URL}/uploads/${normalizedUrl}`;
  } catch (error) {
    console.error('[Dashboard] Error parsing metadata:', error);
    return null;
  }
};

const readArtworkCache = () => {
  try {
    const raw = localStorage.getItem(ARTWORK_CACHE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const getCachedArtworkUrl = (fetchKey) => {
  const entry = readArtworkCache()[fetchKey];
  if (entry && entry.url && Date.now() - (entry.ts || 0) < ARTWORK_CACHE_TTL_MS) {
    return entry.url;
  }
  return null;
};

const saveCachedArtworkUrl = (fetchKey, url) => {
  try {
    const cache = readArtworkCache();
    cache[fetchKey] = { url, ts: Date.now() };
    const keys = Object.keys(cache);
    if (keys.length > ARTWORK_CACHE_MAX_ENTRIES) {
      keys
        .sort((a, b) => (cache[a].ts || 0) - (cache[b].ts || 0))
        .slice(0, keys.length - ARTWORK_CACHE_MAX_ENTRIES)
        .forEach((key) => delete cache[key]);
    }
    localStorage.setItem(ARTWORK_CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage unavailable/full – cache is best-effort only
  }
};

function EqualizerBars({ color = '#22d3ee', height = 14, barCount = 4, animated = true }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: '2.5px', height }}>
      {Array.from({ length: barCount }).map((_, i) => (
        <Box
          key={i}
          className="dashboard-eq-bar"
          sx={{
            width: 3,
            height: '100%',
            borderRadius: 1,
            bgcolor: color,
            transformOrigin: 'bottom',
            animation: animated ? `eq-bounce ${0.8 + i * 0.12}s ease-in-out ${i * 0.14}s infinite` : 'none',
            transform: animated ? undefined : 'scaleY(0.35)',
          }}
        />
      ))}
    </Box>
  );
}

// Decorative waveform strip along the bottom of the hero — pure CSS motion,
// deterministic per-bar timing so re-renders don't restart the animation.
function WaveformStrip({ color = 'rgba(34,211,238,0.5)', barCount = 56, height = 30 }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height,
        display: 'flex',
        alignItems: 'flex-end',
        gap: '3px',
        px: 2,
        opacity: 0.35,
        pointerEvents: 'none',
        maskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
        WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
      }}
    >
      {Array.from({ length: barCount }).map((_, i) => {
        const pseudo = (i * 37) % 11; // deterministic 0..10 spread
        return (
          <Box
            key={i}
            className="dashboard-eq-bar"
            sx={{
              flex: 1,
              minWidth: 2,
              height: '100%',
              borderRadius: '2px 2px 0 0',
              background: `linear-gradient(180deg, ${color}, transparent)`,
              transformOrigin: 'bottom',
              animation: `eq-bounce ${0.9 + pseudo * 0.09}s ease-in-out ${pseudo * 0.07}s infinite`,
            }}
          />
        );
      })}
    </Box>
  );
}

// Tracks load state itself rather than using MUI Avatar: Avatar's hidden probe
// image can swap the <img> for an alt-text letter drawn in the background
// colour (a blank black square) without the <img> ever firing onError.
function HeroArtwork({ src, alt, onError, isDark }) {
  const [loadedSrc, setLoadedSrc] = useState(null);
  const showImage = Boolean(src) && loadedSrc === src;
  return (
    <Box
      sx={{
        position: 'relative',
        width: { xs: 140, sm: 208 },
        height: { xs: 140, sm: 208 },
        borderRadius: 3,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.75,
        background: 'linear-gradient(135deg, #1b2535 0%, #0f1622 100%)',
        border: `1px solid ${showImage ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.10)'}`,
        boxShadow: showImage
          ? (isDark
            ? '0 24px 60px -18px rgba(0,0,0,0.9), 0 0 44px -10px rgba(34,211,238,0.35)'
            : '0 24px 60px -24px rgba(15,23,42,0.45)')
          : '0 24px 60px -18px rgba(0,0,0,0.85)',
      }}
    >
      {!showImage && (
        <>
          <MusicNoteIcon sx={{ fontSize: 56, color: 'rgba(148,163,184,0.5)' }} />
          <EqualizerBars color="#22d3ee" height={16} />
        </>
      )}
      {src && (
        <Box
          key={src}
          component="img"
          src={src}
          alt={alt}
          onLoad={() => setLoadedSrc(src)}
          onError={() => onError(src)}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: showImage ? 1 : 0,
            transition: 'opacity 240ms ease',
          }}
        />
      )}
    </Box>
  );
}

const capitalize = (value) => {
  const text = String(value || '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
};

const greetingForHour = (hour) => {
  if (hour < 5) return 'Good night';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

export default function Dashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const mixblockColor = theme.palette.brand.mixblock;
  const accentColor = 'var(--rs-accent)';
  const onAirColor = theme.palette.brand.onAir;
  const mixblockChipSx = {
    bgcolor: 'var(--rs-live-chip-program-bg)',
    color: 'var(--rs-live-chip-program-fg)',
    fontWeight: 700,
    border: '1px solid var(--rs-live-chip-program-fg)',
  };
  const { user } = useAuth();
  // statusAll: { [stationId]: status } when using all=1
  const [statusAll, setStatusAll] = useState({});
  const [progressByStation, setProgressByStation] = useState({});
  const [stations, setStations] = useState([]);
  // The grid drops (and saves) any widget it has no card for, so it must not
  // mount until the station heroes exist.
  const [stationsLoaded, setStationsLoaded] = useState(false);
  const [stationsError, setStationsError] = useState(false);
  const [regionsByStation, setRegionsByStation] = useState({}); // { [stationId]: regions[] }
  const [regionalNowPlayingByStation, setRegionalNowPlayingByStation] = useState({}); // { [stationId]: { regions } }
  const [regionalStreamStatusByStation, setRegionalStreamStatusByStation] = useState({}); // { [stationId]: statuses[] }
  const [assetDataByStation, setAssetDataByStation] = useState({});
  const [categoryDataByStation, setCategoryDataByStation] = useState({});
  const [fetchedArtworkUrlByStation, setFetchedArtworkUrlByStation] = useState({});
  const [failedArtworkUrls, setFailedArtworkUrls] = useState({}); // { [url]: true } for URLs that failed to load
  const assetLookupKeyRef = useRef({});
  const fetchedArtworkLookupKeyRef = useRef({});
  const artworkFetchAttemptsRef = useRef({}); // { [stationId]: { key, count } }
  const latestSocketStatusAtRef = useRef({});
  const statusFetchInFlightRef = useRef(false);
  const [primaryStationId, setPrimaryStationId] = useState(null); // first station for hero artwork
  const [playlistGeneratedUntilByStation, setPlaylistGeneratedUntilByStation] = useState({}); // { [stationId]: "YYYY-MM-DD" }
  const [gridLayouts, setGridLayouts] = useState({ lg: [], xs: [] });
  const [removedWidgets, setRemovedWidgets] = useState({});
  const [isLayoutEditing, setIsLayoutEditing] = useState(false);
  const [useServerLayout, setUseServerLayout] = useState(false);
  const [layoutPreferencesLoaded, setLayoutPreferencesLoaded] = useState(false);
  const legacyLayoutOrderRef = useRef(null); // pre-grid saved order, migrated once stations load
  const { width: gridWidth, containerRef: gridContainerRef, mounted: gridMounted } = useContainerWidth();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const getLocalLayoutFallback = useCallback(() => {
    const savedPrimaryStationId = localStorage.getItem(PRIMARY_STATION_STORAGE_KEY);
    const savedOrderRaw = localStorage.getItem(LAYOUT_ORDER_STORAGE_KEY);
    const savedHiddenRaw = localStorage.getItem(HIDDEN_WIDGETS_STORAGE_KEY);
    const savedGridRaw = localStorage.getItem(GRID_LAYOUTS_STORAGE_KEY);

    let savedOrder = [];
    let savedHidden = {};
    let savedGrid = null;

    if (savedOrderRaw) {
      try {
        const parsed = JSON.parse(savedOrderRaw);
        if (Array.isArray(parsed)) savedOrder = parsed;
      } catch (error) {
        console.error('Dashboard: failed to parse fallback layout order', error);
      }
    }

    if (savedHiddenRaw) {
      try {
        const parsed = JSON.parse(savedHiddenRaw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          Object.keys(parsed).forEach((key) => {
            if (parsed[key] === true) {
              savedHidden[key] = true;
            }
          });
        }
      } catch (error) {
        console.error('Dashboard: failed to parse fallback hidden widgets', error);
      }
    }

    if (savedGridRaw) {
      try {
        const parsed = JSON.parse(savedGridRaw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Array.isArray(parsed.lg) && parsed.lg.length > 0) {
          savedGrid = parsed;
        }
      } catch (error) {
        console.error('Dashboard: failed to parse fallback grid layouts', error);
      }
    }

    let parsedPrimaryStationId = null;
    let hasSavedPrimaryStationId = false;
    if (savedPrimaryStationId != null) {
      hasSavedPrimaryStationId = true;
      const parsed = parseInt(savedPrimaryStationId, 10);
      parsedPrimaryStationId = Number.isInteger(parsed) ? parsed : savedPrimaryStationId;
    }

    return {
      layoutOrder: savedOrder,
      hiddenWidgets: savedHidden,
      gridLayouts: savedGrid,
      hasSavedPrimaryStationId,
      primaryStationId: parsedPrimaryStationId,
    };
  }, []);

  // Applies a loaded preference set (server or local). Prefers the saved widget
  // grid; falls back to the pre-grid ordered list, which is migrated into a
  // grid once stations are known (see the reconcile effect below).
  const applyLayoutPreferences = useCallback(({ gridLayouts: grid, layoutOrder, hiddenWidgets: hidden, hasPrimary, primary }) => {
    setRemovedWidgets(hidden || {});
    if (grid && Array.isArray(grid.lg) && grid.lg.length > 0) {
      setGridLayouts({ lg: grid.lg, xs: Array.isArray(grid.xs) ? grid.xs : [] });
    } else if (Array.isArray(layoutOrder) && layoutOrder.length > 0) {
      legacyLayoutOrderRef.current = layoutOrder;
    }
    if (hasPrimary) {
      setPrimaryStationId(primary);
    }
  }, []);

  const fetchDashboardLayout = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard-layout`, { withCredentials: true });
      const data = res.data || {};

      setUseServerLayout(true);
      const normalizedHidden = {};
      if (data.hiddenWidgets && typeof data.hiddenWidgets === 'object' && !Array.isArray(data.hiddenWidgets)) {
        Object.keys(data.hiddenWidgets).forEach((key) => {
          if (data.hiddenWidgets[key] === true) {
            normalizedHidden[key] = true;
          }
        });
      }

      const serverGridLayouts =
        data.gridLayouts && typeof data.gridLayouts === 'object' && !Array.isArray(data.gridLayouts)
          ? data.gridLayouts
          : null;
      const serverLayoutOrder = Array.isArray(data.layoutOrder) ? data.layoutOrder : [];
      const hasServerPrimaryStationId = data.primaryStationId != null;
      const parsedServerPrimaryStationId = hasServerPrimaryStationId
        ? (() => {
            const parsedStationId = parseInt(data.primaryStationId, 10);
            return Number.isInteger(parsedStationId) ? parsedStationId : data.primaryStationId;
          })()
        : null;
      const hasServerPreferences = (
        (serverGridLayouts && Array.isArray(serverGridLayouts.lg) && serverGridLayouts.lg.length > 0) ||
        serverLayoutOrder.length > 0 ||
        Object.keys(normalizedHidden).length > 0 ||
        hasServerPrimaryStationId
      );

      if (hasServerPreferences) {
        applyLayoutPreferences({
          gridLayouts: serverGridLayouts,
          layoutOrder: serverLayoutOrder,
          hiddenWidgets: normalizedHidden,
          hasPrimary: hasServerPrimaryStationId,
          primary: parsedServerPrimaryStationId,
        });
      } else {
        // Keep previously saved local prefs if server returns an empty/default layout.
        const localFallback = getLocalLayoutFallback();
        applyLayoutPreferences({
          gridLayouts: localFallback.gridLayouts,
          layoutOrder: localFallback.layoutOrder,
          hiddenWidgets: localFallback.hiddenWidgets,
          hasPrimary: localFallback.hasSavedPrimaryStationId,
          primary: localFallback.primaryStationId,
        });
      }
    } catch (e) {
      console.warn('Dashboard: server layout unavailable, using local fallback', e?.message || e);
      setUseServerLayout(false);
      const localFallback = getLocalLayoutFallback();
      applyLayoutPreferences({
        gridLayouts: localFallback.gridLayouts,
        layoutOrder: localFallback.layoutOrder,
        hiddenWidgets: localFallback.hiddenWidgets,
        hasPrimary: localFallback.hasSavedPrimaryStationId,
        primary: localFallback.primaryStationId,
      });
    } finally {
      setLayoutPreferencesLoaded(true);
    }
  }, [getLocalLayoutFallback, applyLayoutPreferences]);

  const fetchStations = useCallback(async () => {
    setStationsError(false);
    try {
      const res = await axios.get(`${API_URL}/api/stations`, { withCredentials: true });
      const list = Array.isArray(res.data) ? res.data : [];
      setStations(list);
      setStationsLoaded(true);
      if (list.length > 0) {
        // Use the functional updater to compare against the most recent state
        // without listing primaryStationId as a dependency (which causes a double-fetch)
        setPrimaryStationId(prev => {
          if (!prev || !list.some((station) => String(station.id) === String(prev))) {
            return list[0].id;
          }
          return prev;
        });
      }
    } catch (e) {
      console.error('Dashboard: fetch stations failed', e);
      setStationsError(true);
    }
  }, []);

  const fetchStatusAll = useCallback(async () => {
    if (statusFetchInFlightRef.current) return;
    statusFetchInFlightRef.current = true;
    const requestStartedAt = Date.now();

    try {
      const res = await axios.get(`${API_URL}/api/stream/status`, {
        params: { all: 1 },
        withCredentials: true,
      });
      const data = res.data;
      const snapshot = (
        data
        && typeof data === 'object'
        && !Array.isArray(data)
        && (data.isPlaying !== undefined || data.currentTrack !== undefined)
      ) ? { 1: data } : (data || {});
      setStatusAll((prev) => mergeDashboardStatusSnapshot(prev, snapshot, {
        requestStartedAt,
        socketUpdatedAt: latestSocketStatusAtRef.current,
      }));
    } catch (e) {
      console.error('Dashboard: fetch status all failed', e);
    } finally {
      statusFetchInFlightRef.current = false;
    }
  }, []);

  const fetchRegionsAndRegionalNowPlaying = useCallback(async (stationId) => {
    if (!stationId) return;
    try {
      const [regRes, nowRes, streamStatusRes] = await Promise.all([
        axios.get(`${API_URL}/api/regions`, { params: { station_id: stationId }, withCredentials: true }),
        axios.get(`${API_URL}/api/regional-streams/now-playing`, { params: { station_id: stationId }, withCredentials: true }).catch(() => ({ data: { regions: [] } })),
        axios.get(`${API_URL}/api/regional-streams/status`, { params: { station_id: stationId }, withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setRegionsByStation((prev) => ({ ...prev, [stationId]: regRes.data || [] }));
      setRegionalNowPlayingByStation((prev) => ({ ...prev, [stationId]: nowRes.data?.regions || [] }));
      setRegionalStreamStatusByStation((prev) => ({ ...prev, [stationId]: streamStatusRes.data || [] }));
    } catch (e) {
      console.error('Dashboard: fetch regions/regional now-playing failed', e);
    }
  }, []);

  useEffect(() => {
    fetchStations();
    fetchDashboardLayout();
  }, [fetchDashboardLayout, fetchStations]);

  useEffect(() => {
    const newSocket = io(API_URL);

    newSocket.on('connect', () => {
      // Refresh once after every connection to recover any transition missed
      // before the socket became available.
      fetchStatusAll();
    });
    newSocket.on('disconnect', () => {
      fetchStatusAll();
    });

    newSocket.on('playout:status', (data) => {
      // Regional mixers share the station id. Their status must not replace
      // the master station the dashboard is showing.
      if (data?.regionId != null) return;
      const sid = data.stationId != null ? data.stationId : 1;
      latestSocketStatusAtRef.current[String(sid)] = Date.now();
      setStatusAll((prev) => {
        const current = prev[sid] || {};
        return mergeDashboardStatusSnapshot(prev, {
          [sid]: { ...current, ...data },
        });
      });
    });

    newSocket.on('playout:progress', (data) => {
      if (data?.regionId != null) return;
      const sid = data.stationId != null ? data.stationId : 1;
      setProgressByStation((prev) => ({
        ...prev,
        [sid]: {
          currentTime: data.currentTime,
          duration: data.track?.duration || 0,
        },
      }));
    });

    fetchStatusAll();
    // Socket events are authoritative while connected. Poll only as a
    // fallback, otherwise cached REST snapshots can make tracks and artwork
    // jump backwards before the next socket event restores them.
    const interval = setInterval(() => {
      if (!newSocket.connected) fetchStatusAll();
    }, 5000);
    return () => {
      newSocket.close();
      clearInterval(interval);
    };
  }, [fetchStatusAll]);

  useEffect(() => {
    stations.forEach((s) => {
      if (s.mode === 'multi-region') fetchRegionsAndRegionalNowPlaying(s.id);
    });
  }, [stations, fetchRegionsAndRegionalNowPlaying]);

  const fetchPlaylistCoverage = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/playlist-generator/coverage`, { withCredentials: true });
      setPlaylistGeneratedUntilByStation(res.data?.coverage || {});
    } catch (e) {
      console.error('Dashboard: fetch playlist coverage failed', e);
    }
  }, []);

  useEffect(() => {
    fetchPlaylistCoverage();
    const interval = setInterval(fetchPlaylistCoverage, 60000);
    return () => clearInterval(interval);
  }, [fetchPlaylistCoverage]);

  // Refresh regional now-playing periodically for multi-region stations
  useEffect(() => {
    const multiRegionStations = stations.filter((s) => s.mode === 'multi-region');
    if (multiRegionStations.length === 0) return;
    const interval = setInterval(() => {
      multiRegionStations.forEach((s) => fetchRegionsAndRegionalNowPlaying(s.id));
    }, 10000);
    return () => clearInterval(interval);
  }, [stations, fetchRegionsAndRegionalNowPlaying]);

  const getDisplayTrackForStatus = useCallback((status) => {
    if (!status || status.isPlaying !== true) {
      return null;
    }
    return status.currentTrack || null;
  }, []);

  const liveStationCount = stations.filter((s) => Boolean(getDisplayTrackForStatus(statusAll[s.id]))).length;

  const resolveAssetForTrack = useCallback(async (currentTrack) => {
    const assetId = currentTrack?.asset_id || currentTrack?.assetId;

    if (!assetId) {
      const title = currentTrack?.title;
      const artist = currentTrack?.artist;

      if (title || artist) {
        try {
          const searchQuery = [artist, title].filter(Boolean).join(' ').trim();
          const searchRes = await axios.get(`${API_URL}/api/assets`, {
            params: { search: searchQuery },
            withCredentials: true,
          });
          if (searchRes.data?.length > 0) {
            const normalizedTitle = String(title || '').trim().toLowerCase();
            const normalizedArtist = String(artist || '').trim().toLowerCase();

            const matchedAsset = searchRes.data.find((a) => {
              const sameTitle = String(a.title || '').trim().toLowerCase() === normalizedTitle;
              if (!sameTitle) return false;
              if (!normalizedArtist) return true;
              return String(a.artist || '').trim().toLowerCase() === normalizedArtist;
            }) || searchRes.data[0];

            if (matchedAsset) {
              const assetRes = await axios.get(`${API_URL}/api/assets/${matchedAsset.id}`, {
                withCredentials: true,
              });
              const asset = assetRes.data;
              return {
                asset,
                category: asset.categories?.[0] || null,
              };
            }
          }
        } catch (e) {
          console.error('[Dashboard] Error searching asset:', e);
        }
      }
      return { asset: null, category: null };
    }

    try {
      const assetRes = await axios.get(`${API_URL}/api/assets/${assetId}`, {
        withCredentials: true,
      });
      const asset = assetRes.data;
      return {
        asset,
        category: asset.categories?.[0] || null,
      };
    } catch (error) {
      console.error('[Dashboard] Error fetching asset:', error);
      return { asset: null, category: null };
    }
  }, []);

  // Fetch per-station asset/category data when tracks change
  useEffect(() => {
    stations.forEach((station) => {
      const stationId = station.id;
      const currentTrack = getDisplayTrackForStatus(statusAll[stationId]);

      if (!currentTrack) {
        delete assetLookupKeyRef.current[stationId];
        delete fetchedArtworkLookupKeyRef.current[stationId];
        setAssetDataByStation((prev) => {
          if (!Object.prototype.hasOwnProperty.call(prev, stationId)) return prev;
          const next = { ...prev };
          delete next[stationId];
          return next;
        });
        setCategoryDataByStation((prev) => {
          if (!Object.prototype.hasOwnProperty.call(prev, stationId)) return prev;
          const next = { ...prev };
          delete next[stationId];
          return next;
        });
        setFetchedArtworkUrlByStation((prev) => {
          if (!Object.prototype.hasOwnProperty.call(prev, stationId)) return prev;
          const next = { ...prev };
          delete next[stationId];
          return next;
        });
        return;
      }

      const lookupKey = [
        currentTrack.asset_id || currentTrack.assetId || currentTrack.id || '',
        currentTrack.title || '',
        currentTrack.artist || '',
      ].join('|');

      if (assetLookupKeyRef.current[stationId] === lookupKey) {
        return;
      }

      assetLookupKeyRef.current[stationId] = lookupKey;

      // Track changed: drop the previous track's asset/category/artwork state
      // immediately so artwork gating never runs off stale categories while
      // the new asset is being resolved
      delete fetchedArtworkLookupKeyRef.current[stationId];
      delete artworkFetchAttemptsRef.current[stationId];
      setAssetDataByStation((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, stationId)) return prev;
        const next = { ...prev };
        delete next[stationId];
        return next;
      });
      setCategoryDataByStation((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, stationId)) return prev;
        const next = { ...prev };
        delete next[stationId];
        return next;
      });
      setFetchedArtworkUrlByStation((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, stationId)) return prev;
        const next = { ...prev };
        delete next[stationId];
        return next;
      });

      (async () => {
        const result = await resolveAssetForTrack(currentTrack);
        // Status polling recreates statusAll while a lookup is in flight. Only
        // discard the result if the station has actually moved to another track.
        if (assetLookupKeyRef.current[stationId] !== lookupKey) return;
        setAssetDataByStation((prev) => ({ ...prev, [stationId]: result.asset }));
        setCategoryDataByStation((prev) => ({ ...prev, [stationId]: result.category }));

        if (!result.asset && assetLookupKeyRef.current[stationId] === lookupKey) {
          delete assetLookupKeyRef.current[stationId];
        }
      })();
    });
  }, [stations, statusAll, resolveAssetForTrack, getDisplayTrackForStatus]);

  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isCommercialTrack = (track) => {
    if (!track || typeof track !== 'object') return false;
    const type = String(track.type || '').toLowerCase();
    const title = String(track.title || '').toLowerCase();
    return (
      type === 'commercial_break' ||
      type === 'ad_break' ||
      type.includes('commercial') ||
      track.commercial_break_id != null ||
      track.isCommercial === true ||
      title.includes('commercial')
    );
  };

  const isMixblockTrack = (track) => {
    if (!track || typeof track !== 'object') return false;
    const type = String(track.type || '').toLowerCase();
    return type === 'mixblock_track' || type === 'mixblock_mix';
  };

  const getTrackDisplay = (track) => {
    if (!track) return { title: 'Unknown', artist: 'Unknown Artist', category: '', isCommercial: false, isMixblock: false };
    if (isCommercialTrack(track)) {
      return { title: 'Commercials', artist: '', category: '', isCommercial: true, isMixblock: false };
    }
    const mixblock = isMixblockTrack(track);
    return {
      title: track.title || 'Unknown',
      artist: track.artist || 'Unknown Artist',
      category: track.category_name || track.category?.name || track.categories?.[0]?.name || '',
      isCommercial: false,
      isMixblock: mixblock,
      mixblockName: mixblock ? (track.mixblock_name || 'Mixblock') : null,
      mixblockPosition: track.mixblock_position,
      mixblockTotal: track.mixblock_total,
      isMixblockMix: String(track.type || '').toLowerCase() === 'mixblock_mix',
    };
  };

  // Artwork only shows when the playing asset belongs to a category with
  // "Enable Artwork Display" switched on (set in category planning).
  const isArtworkEnabledForStation = useCallback((stationId) => {
    const asset = assetDataByStation[stationId];
    if (asset && Array.isArray(asset.categories) && asset.categories.length > 0) {
      return asset.categories.some((category) => isTruthyFlag(category?.artwork_enabled));
    }
    return isTruthyFlag(categoryDataByStation[stationId]?.artwork_enabled);
  }, [assetDataByStation, categoryDataByStation]);

  const markArtworkUrlFailed = useCallback((url) => {
    if (!url) return;
    setFailedArtworkUrls((prev) => {
      if (prev[url]) return prev;
      // A newly-broken image is a fresh reason to look online, so lift the
      // per-track fetch cap (idempotent, safe if the updater runs twice)
      artworkFetchAttemptsRef.current = {};
      return { ...prev, [url]: true };
    });
  }, []);

  // Fetch per-station artwork from online source when metadata artwork is
  // unavailable or its URL failed to load (broken image → re-look online)
  useEffect(() => {
    stations.forEach((station) => {
      const stationId = station.id;
      const assetData = assetDataByStation[stationId];
      const currentTrack = getDisplayTrackForStatus(statusAll[stationId]);

      const clearFetchedUrl = () => {
        setFetchedArtworkUrlByStation((prev) => {
          if (!Object.prototype.hasOwnProperty.call(prev, stationId)) return prev;
          const next = { ...prev };
          delete next[stationId];
          return next;
        });
      };

      if (!currentTrack || !isArtworkEnabledForStation(stationId)) {
        delete fetchedArtworkLookupKeyRef.current[stationId];
        delete artworkFetchAttemptsRef.current[stationId];
        clearFetchedUrl();
        return;
      }

      // A metadata artwork URL only counts if it hasn't failed to load
      const usableMetadataUrl = [assetData, assetData?.metadata, currentTrack, currentTrack?.metadata]
        .map(resolveMetadataArtworkUrl)
        .find((url) => url && !failedArtworkUrls[url]);

      if (usableMetadataUrl) {
        clearFetchedUrl();
        return;
      }

      const title = assetData?.title || currentTrack?.title;
      const artist = assetData?.artist || currentTrack?.artist;
      const album = assetData?.album || currentTrack?.album;

      if (!title || !artist) {
        clearFetchedUrl();
        return;
      }

      const fetchKey = `${title}|${artist}|${album || ''}`;

      // Serve from the local cache so artwork shows immediately after a reload
      const cachedUrl = getCachedArtworkUrl(fetchKey);
      if (cachedUrl && !failedArtworkUrls[cachedUrl]) {
        fetchedArtworkLookupKeyRef.current[stationId] = fetchKey;
        setFetchedArtworkUrlByStation((prev) => (
          prev[stationId] === cachedUrl ? prev : { ...prev, [stationId]: cachedUrl }
        ));
        return;
      }

      if (fetchedArtworkLookupKeyRef.current[stationId] === fetchKey) {
        const storedUrl = fetchedArtworkUrlByStation[stationId];
        if (!(storedUrl && failedArtworkUrls[storedUrl])) {
          return;
        }
        // The URL we fetched earlier is broken — re-look for it
        delete fetchedArtworkLookupKeyRef.current[stationId];
      }

      // Cap retries per track so a missing artwork doesn't hammer the API every poll
      const attempts = artworkFetchAttemptsRef.current[stationId];
      const attemptCount = attempts && attempts.key === fetchKey ? attempts.count : 0;
      if (attemptCount >= ARTWORK_FETCH_MAX_ATTEMPTS) {
        return;
      }
      artworkFetchAttemptsRef.current[stationId] = { key: fetchKey, count: attemptCount + 1 };

      fetchedArtworkLookupKeyRef.current[stationId] = fetchKey;

      (async () => {
        try {
          const response = await axios.get(`${API_URL}/api/artwork/fetch`, {
            params: { title, artist, album },
            withCredentials: true,
          });

          // Ignore only genuinely stale requests, not ordinary status refreshes.
          if (fetchedArtworkLookupKeyRef.current[stationId] !== fetchKey) return;
          if (response.data && response.data.artwork_url) {
            saveCachedArtworkUrl(fetchKey, response.data.artwork_url);
            setFetchedArtworkUrlByStation((prev) => ({ ...prev, [stationId]: response.data.artwork_url }));
          } else {
            // Nothing found — clear the lookup key so a later cycle re-looks
            if (fetchedArtworkLookupKeyRef.current[stationId] === fetchKey) {
              delete fetchedArtworkLookupKeyRef.current[stationId];
            }
            clearFetchedUrl();
          }
        } catch (error) {
          if (fetchedArtworkLookupKeyRef.current[stationId] !== fetchKey) return;
          console.error('[Dashboard] Error fetching artwork from online source:', error);
          delete fetchedArtworkLookupKeyRef.current[stationId];
          clearFetchedUrl();
        }
      })();
    });
  }, [stations, statusAll, assetDataByStation, fetchedArtworkUrlByStation, failedArtworkUrls, getDisplayTrackForStatus, isArtworkEnabledForStation]);

  const getArtworkUrlForStation = (stationId) => {
    if (!isArtworkEnabledForStation(stationId)) return null;

    const assetData = assetDataByStation[stationId];
    const currentTrack = getDisplayTrackForStatus(statusAll[stationId]);

    // Check direct fields and metadata, skipping broken URLs
    const metadataCandidates = [assetData, assetData?.metadata, currentTrack, currentTrack?.metadata];
    for (const rawMetadata of metadataCandidates) {
      const url = resolveMetadataArtworkUrl(rawMetadata);
      if (url && !failedArtworkUrls[url]) {
        return url;
      }
    }

    // Fallback to fetched artwork from online source
    const fetchedUrl = fetchedArtworkUrlByStation[stationId];
    if (fetchedUrl && !failedArtworkUrls[fetchedUrl]) {
      return fetchedUrl;
    }

    return null;
  };


  const trackProgress = (track, progress) => {
    const duration = Number(track?.duration || progress?.duration || 0);
    if (!(duration > 0) || !progress || progress.currentTime == null) {
      return { percent: null, remaining: 0, duration };
    }
    return {
      percent: Math.min(100, Math.max(0, (progress.currentTime / duration) * 100)),
      remaining: Math.max(0, duration - (progress.currentTime || 0)),
      duration,
    };
  };

  const formatPlaylistGeneratedUntil = (cov) => {
    if (!cov) return '';
    if (typeof cov === 'string') return cov;
    const { date, hour } = cov;
    if (!date) return '';
    const hourStr = hour != null ? ` ${hour.toString().padStart(2, '0')}:00` : '';
    return `${date}${hourStr}`;
  };

  // Every station has a now-playing hero (on by default). Overview widgets
  // stay in the catalogue so they can be turned on from Customize.
  const allWidgets = buildDashboardWidgets(stations);

  const widgetsById = {};
  allWidgets.forEach((widget) => {
    widgetsById[widget.id] = widget;
  });

  // Apply the station-hero default before paint so a saved "Top Now Playing"
  // layout doesn't flash an empty dashboard.
  if (layoutPreferencesLoaded && stations.length > 0) {
    const result = reconcileDashboardLayout({
      layouts: gridLayouts,
      removedWidgets,
      widgets: allWidgets,
      legacyOrder: legacyLayoutOrderRef.current,
    });
    if (result.consumeLegacyOrder) legacyLayoutOrderRef.current = null;
    if (result.layoutsChanged) setGridLayouts(result.layouts);
    if (result.removedChanged) setRemovedWidgets(result.removedWidgets);
  }

  useEffect(() => {
    if (!layoutPreferencesLoaded) return;

    // Ordered list kept for backwards compatibility with older clients/API consumers
    const layoutOrder = sortGridItems(gridLayouts.lg || []).map((item) => item.i);

    if (primaryStationId != null) {
      localStorage.setItem(PRIMARY_STATION_STORAGE_KEY, String(primaryStationId));
    }
    localStorage.setItem(LAYOUT_ORDER_STORAGE_KEY, JSON.stringify(layoutOrder));
    localStorage.setItem(HIDDEN_WIDGETS_STORAGE_KEY, JSON.stringify(removedWidgets));
    localStorage.setItem(GRID_LAYOUTS_STORAGE_KEY, JSON.stringify(gridLayouts));

    if (!useServerLayout) return;

    const timeoutId = setTimeout(() => {
      axios.put(
        `${API_URL}/api/dashboard-layout`,
        {
          layoutOrder,
          hiddenWidgets: removedWidgets,
          gridLayouts,
          primaryStationId,
        },
        { withCredentials: true }
      ).catch((error) => {
        console.error('Dashboard: failed to save server layout', error);
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [gridLayouts, removedWidgets, primaryStationId, useServerLayout, layoutPreferencesLoaded]);

  const handleLayoutChange = useCallback((_currentLayout, allLayouts) => {
    if (!layoutPreferencesLoaded) return;
    setGridLayouts((prev) => {
      const next = {
        lg: Array.isArray(allLayouts?.lg) ? allLayouts.lg : prev.lg,
        xs: Array.isArray(allLayouts?.xs) ? allLayouts.xs : prev.xs,
      };
      return layoutsAreEqual(prev, next) ? prev : next;
    });
  }, [layoutPreferencesLoaded]);

  const addWidget = (widgetId) => {
    const widget = widgetsById[widgetId];
    if (!widget) return;

    setRemovedWidgets((prev) => {
      if (!prev[widgetId]) return prev;
      const next = { ...prev };
      delete next[widgetId];
      return next;
    });

    setGridLayouts((prev) => {
      if ((prev.lg || []).some((item) => item.i === widgetId)) return prev;
      const size = sizeForWidget(widget);
      const lgBottom = (prev.lg || []).reduce((max, item) => Math.max(max, item.y + item.h), 0);
      const xsBottom = (prev.xs || []).reduce((max, item) => Math.max(max, item.y + item.h), 0);
      return {
        lg: [...(prev.lg || []), { i: widgetId, x: 0, y: lgBottom, w: size.w, h: size.h, minW: size.minW, minH: size.minH }],
        xs: [...(prev.xs || []), { i: widgetId, x: 0, y: xsBottom, w: GRID_COLS.xs, h: size.h, minW: 1, minH: size.minH }],
      };
    });
  };

  const removeWidget = (widgetId) => {
    setRemovedWidgets((prev) => ({ ...prev, [widgetId]: true }));
    setGridLayouts((prev) => ({
      lg: (prev.lg || []).filter((item) => item.i !== widgetId),
      xs: (prev.xs || []).filter((item) => item.i !== widgetId),
    }));
  };

  const toggleWidget = (widgetId) => {
    const onDashboard = (gridLayouts.lg || []).some((item) => item.i === widgetId);
    if (onDashboard) removeWidget(widgetId);
    else addWidget(widgetId);
  };

  const setWidgetsEnabled = (widgets, enabled) => {
    widgets.forEach((widget) => {
      const onDashboard = (gridLayouts.lg || []).some((item) => item.i === widget.id);
      if (enabled && !onDashboard) addWidget(widget.id);
      if (!enabled && onDashboard) removeWidget(widget.id);
    });
  };

  const resetLayout = () => {
    legacyLayoutOrderRef.current = null;
    localStorage.removeItem(LAYOUT_ORDER_STORAGE_KEY);
    localStorage.removeItem(HIDDEN_WIDGETS_STORAGE_KEY);
    localStorage.removeItem(GRID_LAYOUTS_STORAGE_KEY);
    setRemovedWidgets({});
    setGridLayouts(buildDefaultLayouts(allWidgets, {}));
  };

  const visibleWidgets = sortGridItems(gridLayouts.lg || [])
    .map((item) => widgetsById[item.i])
    .filter(Boolean);

  const renderStationFooter = (station) => {
    const coverage = playlistGeneratedUntilByStation[station.id];
    const regions = regionsByStation[station.id] || [];
    const regionalNowPlaying = regionalNowPlayingByStation[station.id] || [];
    const regionalStreamStatus = regionalStreamStatusByStation[station.id] || [];
    const isMultiRegion = station.mode === 'multi-region' && (regions.length > 0 || regionalNowPlaying.length > 0);
    if (!coverage && !isMultiRegion) return null;

    return (
      <Box sx={{ mt: 2.5 }}>
        {coverage && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: isMultiRegion ? 1.5 : 0 }}>
            Playlists generated until {formatPlaylistGeneratedUntil(coverage)}
          </Typography>
        )}
        {isMultiRegion && (
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: accentColor, fontWeight: 800, letterSpacing: '0.08em' }}>
              REGIONS & SPLITS
            </Typography>
            <Box sx={{ display: 'grid', gap: 1 }}>
              {(regionalNowPlaying.length > 0 ? regionalNowPlaying : regions.map((r) => ({ regionId: r.id, regionName: r.name, regionCode: r.code, currentTrack: null, inSync: null }))).map((reg) => {
                const streamStatus = regionalStreamStatus.find((rs) => rs.regionId === reg.regionId);
                const regionRow = regions.find((r) => r.id === reg.regionId);
                const timeShiftHours = Number(regionRow?.time_shift_hours) || 0;
                const isRunning = streamStatus?.status === 'running';
                return (
                  <Box
                    key={reg.regionId}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: 'rgba(148,163,184,0.05)',
                      border: '1px solid rgba(148,163,184,0.10)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {isRunning && <EqualizerBars color="#4ade80" height={10} />}
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {reg.regionName}
                        {reg.regionCode ? ` (${reg.regionCode})` : ''}
                      </Typography>
                      {streamStatus && (
                        <Chip
                          size="small"
                          label={streamStatus.status}
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: isRunning ? 'rgba(34,197,94,0.16)' : 'rgba(148,163,184,0.12)',
                            color: isRunning ? '#4ade80' : '#999',
                            border: isRunning ? '1px solid rgba(34,197,94,0.35)' : '1px solid rgba(148,163,184,0.2)',
                          }}
                        />
                      )}
                      {timeShiftHours > 0 && (
                        <Chip
                          size="small"
                          label={`-${timeShiftHours}h shift`}
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(59,130,246,0.16)',
                            color: '#93c5fd',
                            border: '1px solid rgba(59,130,246,0.35)',
                          }}
                        />
                      )}
                      {reg.inSync === false && !streamStatus?.usingFallback && timeShiftHours === 0 && (
                        <Chip size="small" label="Out of sync" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(245,158,11,0.2)', color: '#f59e0b' }} />
                      )}
                    </Box>
                    {reg.currentTrack ? (
                      <Typography variant="body2" sx={{ mt: 0.25, color: getTrackDisplay(reg.currentTrack).isMixblock ? mixblockColor : 'text.secondary' }} noWrap>
                        {(() => {
                          const regTrackDisplay = getTrackDisplay(reg.currentTrack);
                          if (regTrackDisplay.isCommercial) return regTrackDisplay.title;
                          if (regTrackDisplay.isMixblock) return `${regTrackDisplay.mixblockName}: ${regTrackDisplay.title} — ${regTrackDisplay.artist}`;
                          return `${regTrackDisplay.title} — ${regTrackDisplay.artist}`;
                        })()}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        No track
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    );
  };


  const renderClockWidget = () => {
    const secondsFraction = (now.getSeconds() + now.getMilliseconds() / 1000) / 60;
    return (
      <Card sx={{ borderRadius: 2, height: '100%', overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Soft radial bloom behind the time */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background: isDark
              ? 'radial-gradient(60% 80% at 50% 110%, rgba(34,211,238,0.12), transparent 70%)'
              : 'radial-gradient(60% 80% at 50% 110%, rgba(8,145,178,0.08), transparent 70%)',
          }}
        />
        <CardContent sx={{ textAlign: 'center', position: 'relative', width: '100%' }}>
          <Typography
            variant="overline"
            sx={{ display: 'block', color: 'text.disabled', mb: 0.5, letterSpacing: '0.22em' }}
          >
            Station clock
          </Typography>
          <Typography
            variant="h3"
            className="tabular-nums"
            sx={{
              fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif',
              fontWeight: 700,
              lineHeight: 1.1,
              background: isDark
                ? 'linear-gradient(160deg, #f8fafc 25%, #67e8f9 90%)'
                : 'linear-gradient(160deg, #0f172a 30%, #0e7490 95%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
            }}
          >
            {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {now.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
          {/* Seconds sweep */}
          <Box
            aria-hidden
            sx={{
              mt: 1.5,
              mx: 'auto',
              width: '70%',
              height: 3,
              borderRadius: 9999,
              bgcolor: isDark ? 'rgba(148,163,184,0.14)' : 'rgba(15,23,42,0.08)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${Math.max(2, secondsFraction * 100)}%`,
                borderRadius: 9999,
                background: 'linear-gradient(90deg, #22d3ee, #3b82f6)',
                boxShadow: isDark ? '0 0 10px rgba(34,211,238,0.6)' : 'none',
                // Skip the transition on minute rollover so the bar snaps back
                // to the start instead of visibly rewinding from ~100% to 0%.
                transition: now.getSeconds() === 0 ? 'none' : 'width 950ms linear',
              }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderOnAirWidget = () => (
    <Card sx={{ borderRadius: 2, height: '100%', overflow: 'auto' }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SensorsIcon fontSize="small" /> On-air summary
        </Typography>
        {stations.length === 0 && (
          <Typography variant="body2" color="text.secondary">No stations configured</Typography>
        )}
        {stations.map((station) => {
          const track = getDisplayTrackForStatus(statusAll[station.id]);
          const display = track ? getTrackDisplay(track) : null;
          return (
            <Box
              key={station.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                mb: 0.75,
                px: 1.25,
                py: 0.9,
                borderRadius: 2,
                bgcolor: track ? (isDark ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.06)') : 'transparent',
                border: '1px solid',
                borderColor: track ? (isDark ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.22)') : 'transparent',
                transition: 'background-color 140ms ease, border-color 140ms ease',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(148,163,184,0.07)' : 'rgba(15,23,42,0.04)',
                },
              }}
            >
              {track ? (
                <EqualizerBars color="#4ade80" height={11} barCount={3} />
              ) : (
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: 'text.disabled' }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                {station.name}
              </Typography>
              <Typography variant="body2" color={track ? 'text.primary' : 'text.secondary'} noWrap sx={{ minWidth: 0, fontWeight: track ? 500 : 400 }}>
                {display
                  ? (display.isCommercial ? display.title : `${display.title} — ${display.artist}`)
                  : 'Off air'}
              </Typography>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );

  const renderPlaylistCoverageWidget = () => (
    <Card sx={{ borderRadius: 2, height: '100%', overflow: 'auto' }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventAvailableIcon fontSize="small" /> Playlist coverage
        </Typography>
        {stations.length === 0 && (
          <Typography variant="body2" color="text.secondary">No stations configured</Typography>
        )}
        {stations.map((station) => {
          const coverage = playlistGeneratedUntilByStation[station.id];
          return (
            <Box
              key={station.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 0.75,
                px: 1.25,
                py: 0.9,
                borderRadius: 2,
                transition: 'background-color 140ms ease',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(148,163,184,0.07)' : 'rgba(15,23,42,0.04)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor: coverage ? 'success.main' : 'warning.main',
                    boxShadow: coverage
                      ? (isDark ? '0 0 8px rgba(34,197,94,0.7)' : 'none')
                      : (isDark ? '0 0 8px rgba(245,158,11,0.7)' : 'none'),
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {station.name}
                </Typography>
              </Box>
              <Typography variant="body2" className="tabular-nums" color={coverage ? 'text.secondary' : 'warning.main'}>
                {coverage ? `until ${formatPlaylistGeneratedUntil(coverage)}` : 'Not generated'}
              </Typography>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );

  const renderNowPlayingHero = (station) => {
    const stationId = station.id;
    const primaryStatus = statusAll[stationId] || null;
    const primaryTrack = getDisplayTrackForStatus(primaryStatus);
    const primaryArtworkUrl = getArtworkUrlForStation(stationId);
    const primaryShowArtwork = Boolean(primaryArtworkUrl);
    const heroDisplay = primaryTrack ? getTrackDisplay(primaryTrack) : null;
    const heroProgress = progressByStation[stationId] || null;
    const heroProgressInfo = trackProgress(primaryTrack, heroProgress);
    const heroProgressPercent = heroProgressInfo.percent;

    return (
    <Card
      sx={{
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        borderColor: primaryTrack
          ? (isDark ? 'rgba(34,211,238,0.30)' : 'rgba(8,145,178,0.32)')
          : undefined,
        boxShadow: primaryTrack && isDark
          ? '0 1px 0 rgba(255,255,255,0.05) inset, 0 24px 60px -24px rgba(0,0,0,0.85), 0 0 48px -12px rgba(34,211,238,0.38)'
          : undefined,
      }}
    >
      {/* Ambient blurred artwork backdrop with a slow Ken Burns drift */}
      {primaryTrack && primaryShowArtwork && primaryArtworkUrl && (
        <Box
          aria-hidden
          className="dashboard-hero-art"
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${primaryArtworkUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(52px) saturate(1.45)',
            opacity: isDark ? 0.45 : 0.35,
            animation: 'hero-art-drift 26s ease-in-out infinite',
            willChange: 'transform',
          }}
        />
      )}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(120deg, var(--rs-hero-start) 0%, var(--rs-hero-mid) 45%, var(--rs-hero-end) 100%)',
        }}
      />
      {/* Soft cyan bloom while live */}
      {primaryTrack && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background: isDark
              ? 'radial-gradient(48% 62% at 88% 0%, rgba(34,211,238,0.16), transparent 70%)'
              : 'radial-gradient(48% 62% at 88% 0%, rgba(8,145,178,0.10), transparent 70%)',
          }}
        />
      )}
      {/* Meter-bridge top trim */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: primaryTrack
            ? 'linear-gradient(90deg, rgba(34,211,238,0.85), rgba(59,130,246,0.35) 45%, transparent 75%)'
            : `linear-gradient(90deg, ${isDark ? 'rgba(148,163,184,0.35)' : 'rgba(15,23,42,0.18)'}, transparent 60%)`,
          boxShadow: primaryTrack && isDark ? '0 0 16px rgba(34,211,238,0.55)' : 'none',
        }}
      />
      {primaryTrack && <WaveformStrip />}
      <CardContent
        sx={{
          position: 'relative',
          p: { xs: 2.5, sm: 3.5 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'safe center',
          overflow: 'auto',
        }}
      >
        {primaryTrack ? (
          <Box sx={{ display: 'flex', gap: { xs: 2.5, sm: 4 }, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <Box sx={{ flexShrink: 0 }}>
              <HeroArtwork
                src={primaryArtworkUrl}
                alt={`${primaryTrack.title} artwork`}
                onError={markArtworkUrlFailed}
                isDark={isDark}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.9,
                    px: 1.4,
                    py: 0.5,
                    borderRadius: 9999,
                    bgcolor: isDark ? 'rgba(255,59,59,0.18)' : 'rgba(225,29,29,0.12)',
                    border: `1px solid ${onAirColor}`,
                    boxShadow: isDark ? '0 0 22px -4px rgba(255,59,59,0.6)' : 'none',
                  }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      bgcolor: onAirColor,
                      animation: 'onair-pulse 1.6s infinite',
                    }}
                  />
                  <Typography variant="caption" sx={{ color: onAirColor, fontWeight: 800, letterSpacing: '0.14em' }}>
                    ON AIR
                  </Typography>
                  <EqualizerBars color={onAirColor} height={11} barCount={3} />
                </Box>
                <Chip size="small" label={station.name} sx={{ bgcolor: 'var(--rs-surface)', color: 'var(--rs-text)', fontWeight: 700, border: '1px solid var(--rs-border)' }} />
                {heroDisplay.isMixblock && (
                  <Chip
                    size="small"
                    label={heroDisplay.isMixblockMix
                      ? `${heroDisplay.mixblockName} — Beat Mixed`
                      : heroDisplay.mixblockName + (heroDisplay.mixblockPosition != null
                        ? ` (${heroDisplay.mixblockPosition + 1}/${heroDisplay.mixblockTotal})`
                        : '')}
                    sx={mixblockChipSx}
                  />
                )}
              </Box>
              <Typography
                variant="h3"
                sx={{
                  mb: 0.75,
                  fontWeight: 800,
                  lineHeight: 1.05,
                  letterSpacing: '-0.02em',
                  color: heroDisplay.isMixblock ? mixblockColor : 'text.primary',
                  textShadow: isDark ? '0 2px 28px rgba(0,0,0,0.55)' : 'none',
                  fontSize: { xs: '1.6rem', sm: '2.4rem' },
                }}
              >
                {heroDisplay.title}
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, color: accentColor, fontWeight: 600 }}>
                {heroDisplay.artist || ' '}
              </Typography>
              {heroProgressPercent != null && (
                <Box sx={{ maxWidth: 520, mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={heroProgressPercent}
                    sx={{
                      height: 5,
                      borderRadius: 3,
                      bgcolor: 'rgba(148,163,184,0.16)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        background: 'linear-gradient(90deg, #22d3ee, #3b82f6)',
                        boxShadow: isDark ? '0 0 12px rgba(34,211,238,0.65)' : '0 0 8px rgba(8,145,178,0.35)',
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" className="tabular-nums" color="text.secondary">
                      {formatTime(heroProgress?.currentTime || 0)}
                    </Typography>
                    <Typography variant="caption" className="tabular-nums" color="text.secondary">
                      -{formatTime(heroProgressInfo.remaining)}
                    </Typography>
                  </Box>
                </Box>
              )}
              {Array.isArray(primaryStatus?.upNext) && primaryStatus.upNext.length > 0 && (
                <Box sx={{ maxWidth: 520 }}>
                  <Typography variant="caption" sx={{ color: accentColor, fontWeight: 800, letterSpacing: '0.12em', display: 'block', mb: 1 }}>
                    UP NEXT
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                    {primaryStatus.upNext.slice(0, 2).map((nextTrack, index) => {
                      const nextDisplay = getTrackDisplay(nextTrack);
                      return (
                        <Box
                          key={`${nextTrack?.id ?? 'up'}-${index}`}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}
                        >
                          <Typography variant="caption" className="tabular-nums" sx={{ color: 'text.disabled', fontWeight: 700 }}>
                            {index + 1}
                          </Typography>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ color: nextDisplay.isMixblock ? mixblockColor : 'text.primary', fontWeight: 600, minWidth: 0 }}
                          >
                            {nextDisplay.isCommercial
                              ? nextDisplay.title
                              : nextDisplay.isMixblock
                                ? `${nextDisplay.mixblockName}: ${nextDisplay.title}`
                                : nextDisplay.title}
                            {!nextDisplay.isCommercial && nextDisplay.artist ? (
                              <Box component="span" sx={{ color: accentColor, fontWeight: 600 }}>
                                {` — ${nextDisplay.artist}`}
                              </Box>
                            ) : null}
                            {!nextDisplay.isCommercial && nextDisplay.category ? (
                              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                {` · ${nextDisplay.category}`}
                              </Box>
                            ) : null}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, py: 3 }}>
            <Box
              className="dashboard-standby-icon"
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                background: 'linear-gradient(135deg, rgba(34,211,238,0.16), rgba(59,130,246,0.08))',
                border: '1px solid rgba(34,211,238,0.32)',
                animation: 'standby-pulse 2.8s ease-in-out infinite',
              }}
            >
              <GraphicEqIcon sx={{ fontSize: 32, color: isDark ? 'rgba(103,232,249,0.8)' : 'rgba(8,145,178,0.8)' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>
                Standing by
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`Nothing is playing on ${station.name} right now`}
              </Typography>
            </Box>
          </Box>
        )}
        {renderStationFooter(station)}
      </CardContent>
    </Card>
    );
  };

  const renderWidgetContent = (widget) => {
    switch (widget.type) {
      case 'station':
        return renderNowPlayingHero(widget.station);
      case 'clock':
        return renderClockWidget();
      case 'on-air':
        return renderOnAirWidget();
      case 'playlist-coverage':
        return renderPlaylistCoverageWidget();
      default:
        return null;
    }
  };

  return (
    <Box>
      <PageHeader
        overline="Overview"
        title={`${greetingForHour(now.getHours())}, ${capitalize(user?.username) || 'there'}`}
        subtitle={
          stations.length === 0
            ? now.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
            : `${now.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' })} · ${liveStationCount} of ${stations.length} station${stations.length === 1 ? '' : 's'} live`
        }
        icon={<GraphicEqIcon />}
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {stations.length > 0 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.9,
                  px: 1.4,
                  py: 0.55,
                  borderRadius: 9999,
                  bgcolor: liveStationCount > 0
                    ? (isDark ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.10)')
                    : (isDark ? 'rgba(148,163,184,0.10)' : 'rgba(15,23,42,0.06)'),
                  border: liveStationCount > 0
                    ? '1px solid rgba(34,197,94,0.45)'
                    : `1px solid ${isDark ? 'rgba(148,163,184,0.25)' : 'rgba(15,23,42,0.14)'}`,
                  boxShadow: liveStationCount > 0 && isDark ? '0 0 18px -4px rgba(34,197,94,0.5)' : 'none',
                }}
              >
                {liveStationCount > 0 ? (
                  <EqualizerBars color="#4ade80" height={12} barCount={3} />
                ) : (
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '0.1em',
                    color: liveStationCount > 0 ? (isDark ? '#4ade80' : '#15803d') : 'text.secondary',
                  }}
                >
                  {liveStationCount > 0 ? `${liveStationCount} LIVE` : 'OFF AIR'}
                </Typography>
              </Box>
            )}
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="h5"
                className="tabular-nums"
                sx={{
                  fontFamily: '"Space Grotesk", "Inter", system-ui, sans-serif',
                  fontWeight: 700,
                  lineHeight: 1,
                  background: isDark
                    ? 'linear-gradient(120deg, #f8fafc 20%, #67e8f9 90%)'
                    : 'linear-gradient(120deg, #0f172a 30%, #0e7490 95%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                }}
              >
                {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {now.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}
              </Typography>
            </Box>
          </Box>
        }
      />

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Button
          variant={isLayoutEditing ? 'contained' : 'text'}
          size="small"
          startIcon={<TuneIcon fontSize="small" />}
          onClick={() => setIsLayoutEditing((prev) => !prev)}
          sx={!isLayoutEditing ? { color: 'text.secondary', '&:hover': { color: 'info.light' } } : undefined}
        >
          {isLayoutEditing ? 'Done' : 'Customize'}
        </Button>
      </Box>

      {isLayoutEditing && (
        <DashboardCustomize
          widgets={allWidgets}
          activeIds={(gridLayouts.lg || []).map((item) => item.i)}
          onToggle={toggleWidget}
          onSetEnabled={setWidgetsEnabled}
          onReset={resetLayout}
        />
      )}

      {stationsError && !stationsLoaded && (
        <Card sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
              Couldn’t load your stations.
            </Typography>
            <Button size="small" variant="outlined" onClick={fetchStations}>
              Try again
            </Button>
          </CardContent>
        </Card>
      )}

      {layoutPreferencesLoaded && stationsLoaded && visibleWidgets.length === 0 && !isLayoutEditing && (
        <Card sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
              {stations.length > 0
                ? 'Your dashboard is empty. Turn station heroes back on to see what’s playing.'
                : 'Your dashboard is empty. Use Customize to add widgets.'}
            </Typography>
            <Button size="small" variant="outlined" startIcon={<TuneIcon fontSize="small" />} onClick={() => setIsLayoutEditing(true)}>
              Customize
            </Button>
          </CardContent>
        </Card>
      )}

      <Box ref={gridContainerRef} className={isLayoutEditing ? 'dashboard-grid dashboard-grid-editing' : 'dashboard-grid'}>
        {gridMounted && layoutPreferencesLoaded && stationsLoaded && (
          <ResponsiveGridLayout
            width={gridWidth}
            layouts={gridLayouts}
            breakpoints={GRID_BREAKPOINTS}
            cols={GRID_COLS}
            rowHeight={GRID_ROW_HEIGHT}
            margin={GRID_MARGIN}
            containerPadding={[0, 0]}
            dragConfig={{ enabled: isLayoutEditing, handle: '.dashboard-drag-handle', cancel: '.widget-no-drag' }}
            resizeConfig={{ enabled: isLayoutEditing, handles: ['se', 'e', 's'] }}
            onLayoutChange={handleLayoutChange}
          >
            {visibleWidgets.map((widget, index) => (
              <div key={widget.id}>
                <Box
                  className="dashboard-widget-enter"
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    cursor: 'default',
                    animation: 'widget-enter 420ms ease-out both',
                    animationDelay: `${Math.min(index, 8) * 60}ms`,
                  }}
                >
                  {renderWidgetContent(widget)}
                  {isLayoutEditing && (
                    <Box
                      className="dashboard-drag-handle"
                      title="Drag to move"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 6,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        maxWidth: '46%',
                        px: 1,
                        py: 0.35,
                        borderRadius: 999,
                        bgcolor: 'var(--rs-overlay-strong)',
                        color: 'var(--rs-text)',
                        border: '1px solid var(--rs-border)',
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                      }}
                    >
                      <DragIndicatorIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                      <Typography variant="caption" noWrap sx={{ fontWeight: 700 }}>
                        {widget.label}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </div>
            ))}
          </ResponsiveGridLayout>
        )}
      </Box>
    </Box>
  );
}
